import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Trash2, AlertTriangle } from "lucide-react";
import RichEditor     from "../components/RichEditor";
import QuizEditor     from "../components/QuizEditor";
import SidebarManager from "../components/SidebarManager";
import StudentStats   from "../components/StudentStats";
import StudentManager from "../components/StudentManager";
import FileUploader   from "../components/FileUploader";
import * as XLSX from "xlsx";
import {
  adminGetClasses,   adminCreateClass,   adminUpdateClass,   adminDeleteClass,
  adminGetArticles,  adminCreateArticle, adminUpdateArticle, adminDeleteArticle,
  adminGetResources, adminCreateResource,adminUpdateResource,adminDeleteResource,
  adminGetStats,     adminGetProgress,
  adminGetQuiz,      adminSaveQuiz,     adminDeleteAllResults,  adminQuizStats,     adminDeleteStudentResult,
  adminGetCollaborators, adminCreateCollaborator, adminUpdateCollaborator, adminDeleteCollaborator,
} from "../services/api";
import "./AdminPanel.css";

const RESOURCE_TYPES = [
  { value:"video",  label:"Video (YouTube)"  },
  { value:"slides", label:"Diapositivas"     },
  { value:"pdf",    label:"PDF"              },
  { value:"link",   label:"Link externo"     },
];

// ── Modal de confirmación de eliminación ──────────────────────────
function ConfirmDeleteModal({ name, type, onConfirm, onCancel }) {
  return (
    <div className="admin-modal-overlay" style={{ zIndex:600 }}>
      <div className="admin-modal" style={{ maxWidth:420, textAlign:"center", padding:"2rem 1.5rem" }}>
        <div style={{ width:56, height:56, borderRadius:"50%", background:"#fde8e8", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.2rem" }}>
          <AlertTriangle size={28} color="#c0392b" strokeWidth={2} />
        </div>
        <h2 style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.1rem", color:"var(--charcoal)", marginBottom:".5rem" }}>
          ¿Eliminar {type}?
        </h2>
        <p style={{ fontFamily:"var(--font-body)", fontSize:"0.85rem", color:"var(--text-muted)", marginBottom:".4rem" }}>
          Estás por eliminar:
        </p>
        <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.95rem", color:"var(--teal-dark)", background:"var(--teal-light)", padding:".5rem 1rem", borderRadius:8, marginBottom:"1.5rem", wordBreak:"break-word" }}>
          {name}
        </p>
        <p style={{ fontFamily:"var(--font-body)", fontSize:"0.78rem", color:"#c0392b", marginBottom:"1.5rem" }}>
          Esta acción no se puede deshacer.
        </p>
        <div style={{ display:"flex", gap:".8rem", justifyContent:"center" }}>
          <button className="btn-outline" onClick={onCancel} style={{ minWidth:100 }}>Cancelar</button>
          <button onClick={onConfirm} style={{ minWidth:100, display:"flex", alignItems:"center", gap:".4rem", justifyContent:"center", background:"#c0392b", color:"#fff", border:"none", borderRadius:6, padding:".55rem 1.2rem", fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.82rem", cursor:"pointer" }}>
            <Trash2 size={15} strokeWidth={2.5} /> Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mini barra horizontal ─────────────────────────────────────────
function Bar({ value, max, color = "var(--teal)", label, sublabel }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: ".7rem" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:3 }}>
        <span style={{ fontFamily:"var(--font-display)", fontSize:"0.78rem", color:"var(--text)", fontWeight:600 }}>{label}</span>
        <span style={{ fontFamily:"var(--font-display)", fontSize:"0.72rem", color:"var(--text-muted)" }}>{sublabel ?? value}</span>
      </div>
      <div style={{ height:8, background:"#e8f0f0", borderRadius:4, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:4, transition:"width .4s ease" }} />
      </div>
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────
function StatCard({ label, value, color = "var(--teal)", icon }) {
  return (
    <div style={{ background:"var(--white)", border:"0.5px solid var(--border)", borderRadius:"var(--card-radius)", padding:"1rem 1.2rem", display:"flex", alignItems:"center", gap:"1rem" }}>
      <div style={{ width:44, height:44, borderRadius:"50%", background:color + "18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.3rem", flexShrink:0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.6rem", color, lineHeight:1 }}>{value}</div>
        <div style={{ fontFamily:"var(--font-display)", fontSize:"0.65rem", color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:.6, marginTop:3 }}>{label}</div>
      </div>
    </div>
  );
}

// ── StatsTab ──────────────────────────────────────────────────────
function StatsTab({ isAdmin }) {
  const [general,  setGeneral]  = useState(null);
  const [progress, setProgress] = useState([]);
  const [classes,  setClasses]  = useState([]);
  const [quizView, setQuizView] = useState(null);
  const [quizStats,setQuizStats]= useState(null);
  const [loading,  setLoading]  = useState(true);
  const [qLoading, setQLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoading(true);
    const requests = [adminGetStats(), adminGetProgress()];
    if (isAdmin) requests.push(adminGetClasses());
    Promise.all(requests)
      .then(([gen, prog, cls]) => {
        setGeneral(gen);
        setProgress(Array.isArray(prog) ? prog : []);
        setClasses(Array.isArray(cls)  ? cls  : []);
      })
      .catch((err) => console.error("Error cargando estadísticas:", err))
      .finally(() => setLoading(false));
  }, []);

  const loadQuizStats = async (classId, title) => {
    setQuizView({ classId, title });
    setQLoading(true);
    setQuizStats(null);
    try { setQuizStats(await adminQuizStats(classId)); }
    catch (err) { console.error(err); }
    finally { setQLoading(false); }
  };

  if (loading) return <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Cargando estadísticas…</p>;
  if (!general) return <p style={{ color:"#c0392b" }}>Error cargando datos.</p>;

  const totalStudents  = progress.length;
  const activeStudents = progress.filter(s => parseInt(s.quizzes_taken) > 0).length;
  const avgCorrect     = totalStudents > 0
    ? Math.round(progress.reduce((sum, s) => sum + parseInt(s.correct || 0), 0) / totalStudents)
    : 0;
  const topStudents = [...progress].sort((a,b) => parseInt(b.correct||0) - parseInt(a.correct||0)).slice(0, 5);
  const maxCorrect  = topStudents[0] ? parseInt(topStudents[0].correct) : 1;
  const classesWithQuiz = classes.filter(c => c.is_published);

  return (
    <div>
      <h2 style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.1rem", marginBottom:"1.2rem" }}>Resumen general</h2>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:".8rem", marginBottom:"2rem" }}>
        <StatCard label="Alumnos"    value={general.users}     color="var(--teal)" icon="👤" />
        <StatCard label="Clases"     value={general.classes}   color="#2980b9"     icon="📚" />
        <StatCard label="Artículos"  value={general.articles}  color="#8e44ad"     icon="📄" />
        <StatCard label="Respuestas" value={general.responses} color="#27ae60"     icon="✏️" />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem", marginBottom:"2rem" }}>
        <div style={{ background:"var(--white)", border:"0.5px solid var(--border)", borderRadius:"var(--card-radius)", padding:"1.2rem" }}>
          <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.82rem", color:"var(--teal-dark)", marginBottom:"1rem", textTransform:"uppercase", letterSpacing:.5 }}>Participación</p>
          <div style={{ display:"flex", gap:"1.5rem", marginBottom:"1.2rem" }}>
            {[["Total alumnos", totalStudents,"var(--teal)"],["Han respondido",activeStudents,"#27ae60"],["Sin actividad",totalStudents-activeStudents,"#8a9baa"]].map(([lbl,val,col]) => (
              <div key={lbl} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.5rem", color:col, lineHeight:1 }}>{val}</div>
                <div style={{ fontFamily:"var(--font-display)", fontSize:"0.62rem", color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:.5, marginTop:3 }}>{lbl}</div>
              </div>
            ))}
          </div>
          <div style={{ height:10, background:"#e8f0f0", borderRadius:5, overflow:"hidden" }}>
            <div style={{ height:"100%", width:totalStudents>0?`${Math.round(activeStudents/totalStudents*100)}%`:"0%", background:"#27ae60", borderRadius:5, transition:"width .4s" }} />
          </div>
          <p style={{ fontFamily:"var(--font-display)", fontSize:"0.68rem", color:"var(--text-muted)", marginTop:5 }}>
            {totalStudents>0?Math.round(activeStudents/totalStudents*100):0}% activos · {avgCorrect} correctas promedio
          </p>
        </div>

        <div style={{ background:"var(--white)", border:"0.5px solid var(--border)", borderRadius:"var(--card-radius)", padding:"1.2rem" }}>
          <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.82rem", color:"var(--teal-dark)", marginBottom:"1rem", textTransform:"uppercase", letterSpacing:.5 }}>Top 5 alumnos</p>
          {topStudents.length === 0 && <p style={{ fontSize:"0.78rem", color:"var(--text-muted)" }}>Sin respuestas aún.</p>}
          {topStudents.map((s,i) => (
            <Bar key={s.id} value={parseInt(s.correct||0)} max={maxCorrect}
              label={`${i+1}. ${s.name||s.email}`} sublabel={`${s.correct} correctas`}
              color={i===0?"var(--gold)":i===1?"var(--teal)":"#8a9baa"} />
          ))}
        </div>
      </div>

      {isAdmin && (
        <div style={{ background:"var(--white)", border:"0.5px solid var(--border)", borderRadius:"var(--card-radius)", padding:"1.2rem", marginBottom:"2rem" }}>
          <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.82rem", color:"var(--teal-dark)", marginBottom:"1rem", textTransform:"uppercase", letterSpacing:.5 }}>Resultados por clase</p>
          <div style={{ display:"flex", gap:".5rem", flexWrap:"wrap", marginBottom:"1.2rem" }}>
            {classesWithQuiz.length===0 && <p style={{ fontSize:"0.78rem", color:"var(--text-muted)" }}>No hay clases publicadas.</p>}
            {classesWithQuiz.map((c) => (
              <button key={c.id} onClick={() => loadQuizStats(c.id, c.title)}
                style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.72rem", padding:"5px 12px", borderRadius:20, border:"0.5px solid var(--border)", background:quizView?.classId===c.id?"var(--teal)":"var(--off-white)", color:quizView?.classId===c.id?"#fff":"var(--text-muted)", cursor:"pointer", transition:"background .15s, color .15s" }}>
                {c.title}
              </button>
            ))}
          </div>
          {quizView && (
            <div>
              <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.85rem", marginBottom:".8rem", color:"var(--charcoal)" }}>{quizView.title}</p>
              {qLoading && <p style={{ fontSize:"0.78rem", color:"var(--text-muted)" }}>Cargando…</p>}
              {!qLoading && quizStats && (
                <>
                  {quizStats.byQuestion?.length>0 && (
                    <div style={{ marginBottom:"1.2rem" }}>
                      <p style={{ fontFamily:"var(--font-display)", fontSize:"0.7rem", color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:.5, marginBottom:".6rem" }}>Por pregunta</p>
                      {quizStats.byQuestion.map((q,i) => {
                        const total=parseInt(q.total_responses)||0, correct=parseInt(q.correct)||0, wrong=parseInt(q.wrong)||0;
                        const pctOk=total>0?Math.round(correct/total*100):0;
                        return (
                          <div key={q.id} style={{ marginBottom:".8rem", padding:".7rem .9rem", background:"var(--off-white)", borderRadius:8 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
                              <span style={{ fontFamily:"var(--font-display)", fontSize:"0.78rem", fontWeight:600 }}>P{i+1}. {q.question.slice(0,60)}{q.question.length>60?"…":""}</span>
                              <span style={{ fontFamily:"var(--font-display)", fontSize:"0.7rem", color:pctOk>=60?"#27ae60":"#c0392b", fontWeight:700 }}>{pctOk}%</span>
                            </div>
                            <div style={{ height:8, background:"#e0e0e0", borderRadius:4, overflow:"hidden", display:"flex" }}>
                              <div style={{ height:"100%", width:`${pctOk}%`, background:"#27ae60", transition:"width .4s" }} />
                              <div style={{ height:"100%", width:`${total>0?Math.round(wrong/total*100):0}%`, background:"#e74c3c", transition:"width .4s" }} />
                            </div>
                            <div style={{ display:"flex", gap:"1rem", marginTop:4 }}>
                              {[["✓ "+correct+" correctas","#27ae60"],["✗ "+wrong+" incorrectas","#e74c3c"],["— "+parseInt(q.empty||0)+" sin resp.","#8a9baa"]].map(([lbl,col])=>(
                                <span key={lbl} style={{ fontFamily:"var(--font-display)", fontSize:"0.65rem", color:col }}>{lbl}</span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {quizStats.byStudent?.length>0 && (
                    <div>
                      <p style={{ fontFamily:"var(--font-display)", fontSize:"0.7rem", color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:.5, marginBottom:".6rem" }}>Por alumno</p>
                      <div style={{ overflowX:"auto" }}>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.78rem" }}>
                          <thead>
                            <tr style={{ background:"#f0f7f7" }}>
                              {["Alumno","Correctas","Incorrectas","Sin resp.","% Acierto"].map(h=>(
                                <th key={h} style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", padding:"6px 10px", textAlign:"left", color:"var(--teal-dark)", borderBottom:"0.5px solid var(--border)" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {quizStats.byStudent.map((s,i) => {
                              const total=parseInt(s.total)||0, pct=total>0?Math.round(parseInt(s.correct)/total*100):0;
                              return (
                                <tr key={s.user_id} style={{ background:i%2===0?"var(--white)":"var(--off-white)" }}>
                                  <td style={{ padding:"6px 10px", borderBottom:"0.5px solid #eef2f2" }}><div style={{ fontWeight:600 }}>{s.user_name||"—"}</div><div style={{ fontSize:"0.68rem", color:"var(--text-muted)" }}>{s.email}</div></td>
                                  <td style={{ padding:"6px 10px", color:"#27ae60", fontWeight:700, borderBottom:"0.5px solid #eef2f2" }}>{s.correct}</td>
                                  <td style={{ padding:"6px 10px", color:"#e74c3c", fontWeight:700, borderBottom:"0.5px solid #eef2f2" }}>{s.wrong}</td>
                                  <td style={{ padding:"6px 10px", color:"#8a9baa", borderBottom:"0.5px solid #eef2f2" }}>{s.empty}</td>
                                  <td style={{ padding:"6px 10px", borderBottom:"0.5px solid #eef2f2" }}>
                                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                      <div style={{ flex:1, height:6, background:"#e0e0e0", borderRadius:3, overflow:"hidden" }}><div style={{ height:"100%", width:`${pct}%`, background:pct>=60?"#27ae60":"#e74c3c", borderRadius:3 }} /></div>
                                      <span style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.72rem", color:pct>=60?"#27ae60":"#e74c3c", minWidth:32 }}>{pct}%</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {quizStats.byQuestion?.length===0 && quizStats.byStudent?.length===0 && (
                    <p style={{ fontSize:"0.78rem", color:"var(--text-muted)" }}>Esta clase no tiene respuestas aún.</p>
                  )}
                </>
              )}
            </div>
          )}
          {!quizView && classesWithQuiz.length>0 && <p style={{ fontSize:"0.78rem", color:"var(--text-muted)" }}>Selecciona una clase para ver resultados.</p>}
        </div>
      )}
    </div>
  );
}

// ── ClassForm ─────────────────────────────────────────────────────
function ClassForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({ title:initial?.title||"", description:initial?.description||"", content:initial?.content||"", thumbnail_url:initial?.thumbnail_url||"", is_published:initial?.is_published??true, order_index:initial?.order_index??0 });
  const [saving,setSaving]=useState(false); const [error,setError]=useState("");
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const handleSubmit=async(e)=>{e.preventDefault();if(!form.title.trim()){setError("El título es requerido");return;}setSaving(true);try{await onSave(form);}catch(err){setError(err.message);setSaving(false);}};
  return (
    <div className="admin-modal-overlay"><div className="admin-modal">
      <h2 className="admin-modal-title">{initial?"Editar":"Crear"} Clase</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group"><label>Título</label><input value={form.title} onChange={(e)=>set("title",e.target.value)} required /></div>
        <div className="form-group"><label>Descripción corta</label><textarea rows={2} value={form.description} onChange={(e)=>set("description",e.target.value)} /></div>
        <div className="form-group"><label>Orden</label><input type="number" min={0} value={form.order_index} onChange={(e)=>set("order_index",parseInt(e.target.value)||0)} /></div>
        <div className="form-group"><label>Imagen de portada</label><FileUploader folder="images" currentUrl={form.thumbnail_url} onUploaded={(url)=>set("thumbnail_url",url)} label="Subir portada" /><input type="url" value={form.thumbnail_url} onChange={(e)=>set("thumbnail_url",e.target.value)} placeholder="O pega una URL..." style={{marginTop:".4rem"}} /></div>
        <div className="form-group"><label>Contenido</label><RichEditor key={initial?.id??"new"} value={form.content} onChange={(html)=>set("content",html)} /></div>
        <div className="form-group" style={{flexDirection:"row",alignItems:"center",gap:".6rem"}}><input type="checkbox" id="pub-c" checked={form.is_published} onChange={(e)=>set("is_published",e.target.checked)} /><label htmlFor="pub-c" style={{margin:0,color:"var(--text)",fontWeight:400}}>Publicado</label></div>
        {error&&<p className="form-error">{error}</p>}
        <div style={{display:"flex",gap:".8rem",justifyContent:"flex-end",marginTop:"1rem"}}><button type="button" onClick={onCancel} className="btn-outline">Cancelar</button><button type="submit" className="btn-gold" disabled={saving}>{saving?"Guardando...":"Guardar"}</button></div>
      </form>
    </div></div>
  );
}

// ── ArticleForm ───────────────────────────────────────────────────
function ArticleForm({ initial, onSave, onCancel }) {
  const [form,setForm]=useState({title:initial?.title||"",description:initial?.description||"",pdf_url:initial?.pdf_url||"",is_published:initial?.is_published??true});
  const [saving,setSaving]=useState(false);const [error,setError]=useState("");
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const handleSubmit=async(e)=>{e.preventDefault();if(!form.title.trim()){setError("El título es requerido");return;}setSaving(true);try{await onSave(form);}catch(err){setError(err.message);setSaving(false);}};
  return (
    <div className="admin-modal-overlay"><div className="admin-modal">
      <h2 className="admin-modal-title">{initial?"Editar":"Crear"} Artículo</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group"><label>Título</label><input value={form.title} onChange={(e)=>set("title",e.target.value)} required /></div>
        <div className="form-group"><label>Descripción corta</label><textarea rows={3} value={form.description} onChange={(e)=>set("description",e.target.value)} /></div>
        <div className="form-group">
          <label>PDF <span style={{fontWeight:400,color:"var(--text-muted)"}}>(requerido)</span></label>
          <FileUploader folder="pdfs" currentUrl={form.pdf_url} onUploaded={(url)=>set("pdf_url",url)} label="Subir PDF" />
          <input type="url" value={form.pdf_url} onChange={(e)=>set("pdf_url",e.target.value)} placeholder="O pega URL de PDF..." style={{marginTop:".4rem"}} />
        </div>
        <div className="form-group" style={{flexDirection:"row",alignItems:"center",gap:".6rem"}}><input type="checkbox" id="pub-a" checked={form.is_published} onChange={(e)=>set("is_published",e.target.checked)} /><label htmlFor="pub-a" style={{margin:0,color:"var(--text)",fontWeight:400}}>Publicado</label></div>
        {error&&<p className="form-error">{error}</p>}
        <div style={{display:"flex",gap:".8rem",justifyContent:"flex-end",marginTop:"1rem"}}><button type="button" onClick={onCancel} className="btn-outline">Cancelar</button><button type="submit" className="btn-gold" disabled={saving}>{saving?"Guardando...":"Guardar"}</button></div>
      </form>
    </div></div>
  );
}

// ── ResourceForm ──────────────────────────────────────────────────
function ResourceForm({ initial, onSave, onCancel }) {
  const [form,setForm]=useState({title:initial?.title||"",description:initial?.description||"",type:initial?.type||"video",url:initial?.url||"",thumbnail_url:initial?.thumbnail_url||"",is_published:initial?.is_published??true});
  const [saving,setSaving]=useState(false);const [error,setError]=useState("");
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const handleSubmit=async(e)=>{e.preventDefault();if(!form.title.trim()){setError("El título es requerido");return;}if(!form.url.trim()){setError("La URL es requerida");return;}setSaving(true);try{await onSave(form);}catch(err){setError(err.message);setSaving(false);}};
  const needsUpload=form.type==="pdf"||form.type==="slides"; const uploadFolder=form.type==="pdf"?"pdfs":"resources";
  const placeholder={video:"https://youtube.com/watch?v=...",slides:"https://docs.google.com/presentation/...",pdf:"O pega URL de PDF...",link:"https://ejemplo.com"}[form.type];
  return (
    <div className="admin-modal-overlay"><div className="admin-modal">
      <h2 className="admin-modal-title">{initial?"Editar":"Crear"} Recurso</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group"><label>Título</label><input value={form.title} onChange={(e)=>set("title",e.target.value)} required /></div>
        <div className="form-group"><label>Tipo</label><select value={form.type} onChange={(e)=>setForm(f=>({...f,type:e.target.value,url:""}))}>{ RESOURCE_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>) }</select></div>
        <div className="form-group"><label>Archivo / URL</label>{needsUpload&&<FileUploader folder={uploadFolder} currentUrl={form.url} onUploaded={(url)=>set("url",url)} label={form.type==="pdf"?"Subir PDF":"Subir archivo"} />}<input type="url" value={form.url} onChange={(e)=>set("url",e.target.value)} placeholder={placeholder} style={{marginTop:needsUpload?".4rem":0}} /></div>
        <div className="form-group"><label>Descripción</label><textarea rows={2} value={form.description} onChange={(e)=>set("description",e.target.value)} /></div>
        <div className="form-group"><label>Miniatura <span style={{fontWeight:400,color:"var(--text-muted)"}}>(opcional)</span></label><FileUploader folder="images" currentUrl={form.thumbnail_url} onUploaded={(url)=>set("thumbnail_url",url)} label="Subir miniatura" /><input type="url" value={form.thumbnail_url} onChange={(e)=>set("thumbnail_url",e.target.value)} placeholder="O pega URL..." style={{marginTop:".4rem"}} /></div>
        <div className="form-group" style={{flexDirection:"row",alignItems:"center",gap:".6rem"}}><input type="checkbox" id="pub-r" checked={form.is_published} onChange={(e)=>set("is_published",e.target.checked)} /><label htmlFor="pub-r" style={{margin:0,color:"var(--text)",fontWeight:400}}>Publicado</label></div>
        {error&&<p className="form-error">{error}</p>}
        <div style={{display:"flex",gap:".8rem",justifyContent:"flex-end",marginTop:"1rem"}}><button type="button" onClick={onCancel} className="btn-outline">Cancelar</button><button type="submit" className="btn-gold" disabled={saving}>{saving?"Guardando...":"Guardar"}</button></div>
      </form>
    </div></div>
  );
}

// ── QuizModal ─────────────────────────────────────────────────────
function QuizModal({ classId, onClose }) {
  const [existing,setExisting]=useState(undefined);const [saved,setSaved]=useState(false);
  useEffect(()=>{adminGetQuiz(classId).then(setExisting).catch(()=>setExisting(null));},[classId]);
  const handleSave=async(data)=>{await adminSaveQuiz(data);setSaved(true);setTimeout(onClose,1200);};
  if(existing===undefined) return <div className="admin-modal-overlay"><div className="admin-modal" style={{textAlign:"center",padding:"3rem"}}>Cargando...</div></div>;
  return (
    <div className="admin-modal-overlay"><div className="admin-modal" style={{maxWidth:860}}>
      {saved?<p style={{textAlign:"center",color:"var(--teal)",padding:"2rem",fontFamily:"var(--font-display)",fontWeight:700}}>✓ Guardado</p>:<QuizEditor classId={classId} initial={existing} onSave={handleSave} onCancel={onClose} />}
    </div></div>
  );
}

function QuizStatsModal({ classId, title, onClose }) {
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [view,        setView]        = useState("questions");
  const [deleting,    setDeleting]    = useState(null);
  const [confirmAll,  setConfirmAll]  = useState(false);

  const load = () => {
    setLoading(true);
    adminQuizStats(classId)
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [classId]);

  const handleDeleteStudent = async (userId, userName) => {
    if (!window.confirm(`¿Eliminar todas las respuestas de ${userName}? Esta acción no se puede deshacer.`)) return;
    setDeleting(userId);
    try {
      await adminDeleteStudentResult(classId, userId);
      load();
    } catch (err) {
      alert("Error eliminando: " + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAll = async () => {
    setConfirmAll(false);
    setDeleting("all");
    try {
      await adminDeleteAllResults(classId);
      load();
    } catch (err) {
      alert("Error eliminando: " + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const downloadXLSX = () => {
    if (!stats) return;
    const questions = stats.byQuestion;
    const students  = stats.byStudent;
    const detail    = stats.detail ?? [];

    const wb = XLSX.utils.book_new();

    const colWidths = [
      { wch: 24 }, { wch: 28 },
      ...questions.map(() => ({ wch: 8 })),
      { wch: 11 }, { wch: 13 }, { wch: 11 }, { wch: 7 }, { wch: 10 },
    ];

    const buildRows = (secStudents) =>
      secStudents.map((s) => {
        const respMap = {};
        detail
          .filter((d) => String(d.user_id) === String(s.user_id))
          .forEach((d) => { respMap[d.question_id] = d; });

        const answerCells = questions.map((q) => {
          const r = respMap[q.id];
          if (!r || r.answer === null) return "—";
          const letra = ["A","B","C","D"][r.answer] ?? r.answer;
          return `${r.is_correct ? "✓" : "✗"} ${letra}`;
        });

        const total   = parseInt(s.total)   || 0;
        const correct = parseInt(s.correct) || 0;
        const pct     = total > 0 ? Math.round((correct / total) * 100) : 0;

        return [
          s.user_name ?? "—", s.email ?? "—",
          ...answerCells,
          correct, parseInt(s.wrong) || 0, parseInt(s.empty) || 0, total, `${pct}%`,
        ];
      });

    const header = [
      "Alumno", "Email",
      ...questions.map((_, i) => `P${i + 1}`),
      "Correctas", "Incorrectas", "Sin resp.", "Total", "% Acierto",
    ];

    const sectionMap = {};
    students.forEach((s) => {
      const sec = s.section_name ?? "Sin sección";
      if (!sectionMap[sec]) sectionMap[sec] = [];
      sectionMap[sec].push(s);
    });

    Object.entries(sectionMap).forEach(([sectionName, secStudents]) => {
      const ws = XLSX.utils.aoa_to_sheet([header, ...buildRows(secStudents)]);
      ws["!cols"] = colWidths;
      const sheetName = sectionName.replace(/[:\\/?*[\]]/g, "").slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    const summaryHeader = [
      "Sección", "Alumno", "Email",
      ...questions.map((_, i) => `P${i + 1}`),
      "Correctas", "Incorrectas", "Sin resp.", "Total", "% Acierto",
    ];

    const summaryRows = students.map((s) => {
      const respMap = {};
      detail
        .filter((d) => String(d.user_id) === String(s.user_id))
        .forEach((d) => { respMap[d.question_id] = d; });

      const answerCells = questions.map((q) => {
        const r = respMap[q.id];
        if (!r || r.answer === null) return "—";
        const letra = ["A","B","C","D"][r.answer] ?? r.answer;
        return `${r.is_correct ? "✓" : "✗"} ${letra}`;
      });

      const total   = parseInt(s.total)   || 0;
      const correct = parseInt(s.correct) || 0;
      const pct     = total > 0 ? Math.round((correct / total) * 100) : 0;

      return [
        s.section_name ?? "Sin sección", s.user_name ?? "—", s.email ?? "—",
        ...answerCells,
        correct, parseInt(s.wrong) || 0, parseInt(s.empty) || 0, total, `${pct}%`,
      ];
    });

    const wsSummary = XLSX.utils.aoa_to_sheet([summaryHeader, ...summaryRows]);
    wsSummary["!cols"] = [
      { wch: 20 }, { wch: 24 }, { wch: 28 },
      ...questions.map(() => ({ wch: 8 })),
      { wch: 11 }, { wch: 13 }, { wch: 11 }, { wch: 7 }, { wch: 10 },
    ];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen");

    XLSX.writeFile(wb, `quiz_${title.replace(/\s+/g, "_").slice(0, 40)}.xlsx`);
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: 820 }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.2rem", flexWrap:"wrap", gap:".5rem" }}>
          <h2 className="admin-modal-title" style={{ margin:0 }}>Stats: {title}</h2>
          <div style={{ display:"flex", gap:".5rem", flexWrap:"wrap" }}>
            <button
              className="btn-primary"
              style={{ fontSize:"0.78rem" }}
              onClick={downloadXLSX}
              disabled={!stats || loading}
            >
              ⬇ Descargar XLSX
            </button>
            <button
              onClick={() => setConfirmAll(true)}
              disabled={deleting === "all" || loading || !stats?.byStudent?.length}
              style={{
                fontSize:"0.78rem", padding:"6px 14px",
                background:"#fde8e8", color:"#c0392b",
                border:"0.5px solid #e8b0b0", borderRadius:6,
                fontFamily:"var(--font-display)", fontWeight:700,
                cursor:"pointer", opacity: (deleting==="all" || !stats?.byStudent?.length) ? 0.5 : 1,
              }}
            >
              {deleting === "all" ? "Eliminando..." : "🗑 Eliminar todos"}
            </button>
            <button className="btn-outline" onClick={onClose}>Cerrar</button>
          </div>
        </div>

        {/* Confirm eliminar todos */}
        {confirmAll && (
          <div style={{ background:"#fde8e8", border:"0.5px solid #e8b0b0", borderRadius:8, padding:"1rem 1.2rem", marginBottom:"1rem", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:".5rem" }}>
            <div>
              <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.85rem", color:"#c0392b", marginBottom:".2rem" }}>
                ¿Eliminar TODAS las respuestas de este quiz?
              </p>
              <p style={{ fontSize:"0.75rem", color:"#a0392b" }}>
                Se borrarán los resultados de {stats?.byStudent?.length ?? 0} alumno(s). Esta acción no se puede deshacer.
              </p>
            </div>
            <div style={{ display:"flex", gap:".5rem" }}>
              <button className="btn-outline" style={{ fontSize:"0.78rem" }} onClick={() => setConfirmAll(false)}>Cancelar</button>
              <button
                onClick={handleDeleteAll}
                style={{ fontSize:"0.78rem", padding:"6px 14px", background:"#c0392b", color:"#fff", border:"none", borderRadius:6, fontFamily:"var(--font-display)", fontWeight:700, cursor:"pointer" }}
              >
                Sí, eliminar todo
              </button>
            </div>
          </div>
        )}

        {loading && <p style={{ color:"var(--text-muted)" }}>Cargando estadísticas...</p>}

        {!loading && stats && (
          <>
            <div style={{ display:"flex", gap:".5rem", marginBottom:"1.2rem" }}>
              {[["questions","Por pregunta"],["students","Por alumno"]].map(([v,lbl]) => (
                <button key={v} className={`admin-tab ${view===v?"active":""}`} onClick={() => setView(v)}>
                  {lbl}
                </button>
              ))}
            </div>

            {/* Vista por pregunta */}
            {view === "questions" && (
              <div className="admin-list">
                {stats.byQuestion.length === 0 && (
                  <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Sin respuestas aún.</p>
                )}
                {stats.byQuestion.map((q, i) => {
                  const total   = parseInt(q.total_responses) || 0;
                  const correct = parseInt(q.correct) || 0;
                  const wrong   = parseInt(q.wrong)   || 0;
                  const pct     = total > 0 ? Math.round((correct / total) * 100) : 0;
                  const opts    = typeof q.options === "string" ? JSON.parse(q.options) : q.options;
                  return (
                    <div key={q.id} style={{ background:"var(--white)", border:"0.5px solid var(--border)", borderRadius:8, padding:"1rem 1.2rem", marginBottom:".6rem" }}>
                      <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.85rem", marginBottom:".6rem" }}>
                        P{i+1}. {q.question}
                      </p>
                      {opts && (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:".3rem", marginBottom:".7rem" }}>
                          {opts.map((o, oi) => (
                            <span key={oi} style={{
                              fontSize:"0.72rem", padding:"2px 10px", borderRadius:10,
                              fontFamily:"var(--font-display)",
                              background: oi === parseInt(q.correct) ? "#e6f9ee" : "var(--off-white)",
                              color:      oi === parseInt(q.correct) ? "#27ae60" : "var(--text-muted)",
                              border:     oi === parseInt(q.correct) ? "0.5px solid #27ae60" : "0.5px solid var(--border)",
                            }}>
                              {["A","B","C","D"][oi]}) {o}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ height:8, background:"#e0e0e0", borderRadius:4, overflow:"hidden", display:"flex", marginBottom:5 }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:"#27ae60", transition:"width .4s" }} />
                        <div style={{ height:"100%", width:`${total>0?Math.round(wrong/total*100):0}%`, background:"#e74c3c" }} />
                      </div>
                      <div style={{ display:"flex", gap:"1rem", alignItems:"center" }}>
                        {[
                          [`✓ ${correct} correctas`,             "#27ae60"],
                          [`✗ ${wrong} incorrectas`,             "#e74c3c"],
                          [`— ${parseInt(q.empty||0)} sin resp.`,"#8a9baa"],
                          [`Total: ${total}`,                    "var(--teal)"],
                        ].map(([lbl,col]) => (
                          <span key={lbl} style={{ fontFamily:"var(--font-display)", fontSize:"0.65rem", color:col, fontWeight:600 }}>{lbl}</span>
                        ))}
                        <span style={{ marginLeft:"auto", fontFamily:"var(--font-display)", fontWeight:800, fontSize:"0.75rem", color: pct>=60?"#27ae60":"#e74c3c" }}>
                          {pct}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Vista por alumno */}
            {view === "students" && (
              <div className="admin-list">
                {stats.byStudent.length === 0 && (
                  <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Nadie ha respondido aún.</p>
                )}
                {stats.byStudent.map((s) => {
                  const total   = parseInt(s.total)   || 0;
                  const correct = parseInt(s.correct) || 0;
                  const pct     = total > 0 ? Math.round((correct / total) * 100) : 0;
                  const det     = (stats.detail ?? []).filter(d => String(d.user_id) === String(s.user_id));

                  return (
                    <div key={s.user_id} style={{ background:"var(--white)", border:"0.5px solid var(--border)", borderRadius:8, padding:"1rem 1.2rem", marginBottom:".6rem" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:".5rem", marginBottom:".8rem" }}>
                        <div>
                          <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.9rem" }}>{s.user_name ?? "—"}</p>
                          <p style={{ fontSize:"0.72rem", color:"var(--text-muted)" }}>{s.email ?? "—"}</p>
                          {s.section_name && (
                            <p style={{ fontSize:"0.68rem", color:"var(--teal)", fontFamily:"var(--font-display)", fontWeight:600, marginTop:2 }}>
                              📚 {s.section_name}
                            </p>
                          )}
                          {s.answered_at && (
                            <p style={{ fontSize:"0.68rem", color:"var(--text-light)", marginTop:2 }}>
                              Respondido: {new Date(s.answered_at).toLocaleDateString("es-PE", { day:"numeric", month:"short", year:"numeric" })}
                            </p>
                          )}
                        </div>
                        <div style={{ display:"flex", gap:".8rem", alignItems:"center", flexWrap:"wrap" }}>
                          {[
                            ["Correctas",   s.correct, "#27ae60"],
                            ["Incorrectas", s.wrong,   "#e74c3c"],
                            ["Sin resp.",   s.empty,   "#8a9baa"],
                          ].map(([lbl,val,col]) => (
                            <div key={lbl} style={{ textAlign:"center" }}>
                              <div style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.2rem", color:col, lineHeight:1 }}>{val}</div>
                              <div style={{ fontFamily:"var(--font-display)", fontSize:"0.58rem", color:"var(--text-muted)", textTransform:"uppercase", marginTop:2 }}>{lbl}</div>
                            </div>
                          ))}
                          <div style={{ textAlign:"center" }}>
                            <div style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.2rem", color:pct>=60?"#27ae60":"#e74c3c", lineHeight:1 }}>{pct}%</div>
                            <div style={{ fontFamily:"var(--font-display)", fontSize:"0.58rem", color:"var(--text-muted)", textTransform:"uppercase", marginTop:2 }}>Acierto</div>
                          </div>
                          <button
                            onClick={() => handleDeleteStudent(s.user_id, s.user_name ?? s.email)}
                            disabled={!!deleting}
                            style={{
                              background:"#fde8e8", color:"#c0392b",
                              border:"0.5px solid #e8b0b0", borderRadius:6,
                              padding:"5px 12px", fontSize:"0.72rem",
                              fontFamily:"var(--font-display)", fontWeight:700,
                              cursor:"pointer", opacity: deleting ? 0.6 : 1,
                            }}
                          >
                            {deleting === s.user_id ? "Eliminando..." : "🗑 Eliminar"}
                          </button>
                        </div>
                      </div>

                      <div style={{ display:"flex", flexDirection:"column", gap:".35rem", borderTop:"0.5px solid var(--border)", paddingTop:".7rem" }}>
                        {stats.byQuestion.map((q, qi) => {
                          const r    = det.find(d => String(d.question_id) === String(q.id));
                          const opts = typeof q.options === "string" ? JSON.parse(q.options) : q.options;
                          const answered = r && r.answer !== null;
                          const letra = answered ? ["A","B","C","D"][r.answer] ?? r.answer : null;
                          const texto = answered ? opts?.[r.answer] ?? "" : null;
                          return (
                            <div key={q.id} style={{ display:"flex", gap:".6rem", alignItems:"baseline", fontSize:"0.76rem" }}>
                              <span style={{ fontFamily:"var(--font-display)", fontWeight:700, color:"var(--text-muted)", minWidth:26, flexShrink:0 }}>P{qi+1}</span>
                              <span style={{ flex:1, color:"var(--text)", lineHeight:1.4 }}>{q.question.slice(0,70)}{q.question.length>70?"…":""}</span>
                              {!r || r.answer === null
                                ? <span style={{ color:"#8a9baa", fontFamily:"var(--font-display)", flexShrink:0 }}>—</span>
                                : <span style={{ fontFamily:"var(--font-display)", fontWeight:700, flexShrink:0, color: r.is_correct ? "#27ae60" : "#e74c3c" }}>
                                    {r.is_correct ? "✓" : "✗"} {letra}) {texto}
                                  </span>
                              }
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── CollaboratorForm ──────────────────────────────────────────────
function CollaboratorForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({ first_name:initial?.first_name||"", last_name:initial?.last_name||"", email:initial?.email||"", password:"" });
  const [saving,setSaving]=useState(false);const [error,setError]=useState("");
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const handleSubmit=async(e)=>{
    e.preventDefault();
    if(!form.first_name||!form.last_name||!form.email){setError("Nombre, apellido y email son requeridos");return;}
    if(!initial&&!form.password){setError("La contraseña es requerida al crear");return;}
    if(form.password&&form.password.length<6){setError("Contraseña mínimo 6 caracteres");return;}
    setSaving(true);
    try{await onSave(form);}catch(err){setError(err.message);setSaving(false);}
  };
  return (
    <div className="admin-modal-overlay"><div className="admin-modal">
      <h2 className="admin-modal-title">{initial?"Editar":"Crear"} Colaborador</h2>
      <form onSubmit={handleSubmit}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
          <div className="form-group"><label>Nombre</label><input value={form.first_name} onChange={e=>set("first_name",e.target.value)} placeholder="Juan" required /></div>
          <div className="form-group"><label>Apellido</label><input value={form.last_name} onChange={e=>set("last_name",e.target.value)} placeholder="Pérez" required /></div>
        </div>
        <div className="form-group"><label>Correo electrónico</label><input type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="colaborador@correo.com" required /></div>
        <div className="form-group">
          <label>{initial?"Nueva contraseña (opcional)":"Contraseña temporal"}</label>
          <input type="password" value={form.password} onChange={e=>set("password",e.target.value)} placeholder="Mínimo 6 caracteres" />
        </div>
        <div style={{background:"var(--teal-light)",borderRadius:8,padding:".8rem 1rem",marginBottom:"1rem",fontSize:"0.78rem",color:"var(--teal-dark)"}}>
          <strong>Permisos del colaborador:</strong> puede crear y editar artículos y recursos, ver estadísticas de alumnos y su progreso. <em>No puede</em> gestionar clases, sidebar, alumnos ni colaboradores.
        </div>
        {error&&<p className="form-error">{error}</p>}
        <div style={{display:"flex",gap:".8rem",justifyContent:"flex-end",marginTop:"1rem"}}>
          <button type="button" onClick={onCancel} className="btn-outline">Cancelar</button>
          <button type="submit" className="btn-gold" disabled={saving}>{saving?"Guardando...":"Guardar"}</button>
        </div>
      </form>
    </div></div>
  );
}

// ══════════════════════════════════════════════════════════════════
export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin        = user?.role === "admin";
  const isCollaborator = user?.role === "collaborator";

  const [tab,           setTab]           = useState("articles");
  const [classes,       setClasses]       = useState([]);
  const [articles,      setArticles]      = useState([]);
  const [resources,     setResources]     = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [modal,         setModal]         = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user) return;
    if (!isAdmin && !isCollaborator) { navigate("/"); return; }
    setTab(isAdmin ? "classes" : "articles");
  }, [user]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTab(tab); }, [tab]);

  const loadTab = async (t) => {
    const noLoad = ["sidebar","students","progress","stats","collaborators_tab"];
    if (noLoad.includes(t)) return;
    setLoading(true);
    try {
      if (t === "classes")   setClasses(await adminGetClasses());
      if (t === "articles")  setArticles(await adminGetArticles());
      if (t === "resources") setResources(await adminGetResources());
    } finally { setLoading(false); }
  };

  const loadCollaborators = async () => {
    setLoading(true);
    try { setCollaborators(await adminGetCollaborators()); }
    finally { setLoading(false); }
  };

  const handleSaveClass = async (form) => {
    modal.item ? await adminUpdateClass(modal.item.id, form) : await adminCreateClass(form);
    setClasses(await adminGetClasses()); setModal(null);
  };
  const handleSaveArticle = async (form) => {
    modal.item ? await adminUpdateArticle(modal.item.id, form) : await adminCreateArticle(form);
    setArticles(await adminGetArticles()); setModal(null);
  };
  const handleSaveResource = async (form) => {
    modal.item ? await adminUpdateResource(modal.item.id, form) : await adminCreateResource(form);
    setResources(await adminGetResources()); setModal(null);
  };
  const handleSaveCollaborator = async (form) => {
    modal.item ? await adminUpdateCollaborator(modal.item.id, form) : await adminCreateCollaborator(form);
    await loadCollaborators(); setModal(null);
  };

  const askDelete = (type, id, name, label) => setConfirmDelete({ type, id, name, label });

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    setConfirmDelete(null);
    if (type === "class")        { await adminDeleteClass(id);        setClasses(classes.filter(c => c.id !== id)); }
    if (type === "article")      { await adminDeleteArticle(id);      setArticles(articles.filter(a => a.id !== id)); }
    if (type === "resource")     { await adminDeleteResource(id);     setResources(resources.filter(r => r.id !== id)); }
    if (type === "collaborator") { await adminDeleteCollaborator(id); setCollaborators(collaborators.filter(c => c.id !== id)); }
  };

  const TABS = [
    ...(isAdmin ? [{ key:"classes",           label:"Clases"        }] : []),
    { key:"articles",              label:"Artículos"     },
    { key:"resources",             label:"Recursos"      },
    ...(isAdmin ? [
      { key:"sidebar",             label:"Sidebar"       },
      { key:"students",            label:"Alumnos"       },
    ] : []),
    { key:"progress",              label:"Progreso"      },
    { key:"stats",                 label:"Estadísticas"  },
    ...(isAdmin ? [{ key:"collaborators_tab", label:"Colaboradores" }] : []),
  ];

  const TYPE_ICONS = { video:"🎥", slides:"📊", pdf:"📄", link:"🔗" };

  return (
    <div className="page admin-page">
      <div className="admin-hero">
        <h1>{isAdmin ? "ADMIN PANEL" : "PANEL COLABORADOR"}</h1>
      </div>

      <div className="admin-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`admin-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => { setTab(t.key); if (t.key === "collaborators_tab") loadCollaborators(); }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="admin-body">
        {loading && <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Cargando...</p>}

        {/* ── Clases ── */}
        {tab === "classes" && isAdmin && !loading && (
          <>
            <div className="admin-toolbar">
              <h2>Clases ({classes.length})</h2>
              <button className="btn-primary" onClick={() => setModal({ type:"class" })}>+ Nueva clase</button>
            </div>
            <div className="admin-list">
              {classes.map(c => (
                <div className="admin-item" key={c.id}>
                  <div className="admin-item-info" style={{ flexDirection:"row", flexWrap:"wrap", gap:".5rem", alignItems:"center" }}>
                    {c.thumbnail_url && <img src={c.thumbnail_url} alt="" style={{ width:44, height:44, objectFit:"cover", borderRadius:6, flexShrink:0 }} />}
                    <div style={{ display:"flex", flexDirection:"column", gap:".2rem", flex:1 }}>
                      <span className="admin-item-title">{c.title}</span>
                      <span className={`admin-badge ${c.is_published?"pub":"draft"}`}>{c.is_published?"Publicado":"Borrador"}</span>
                    </div>
                  </div>
                  <div className="admin-item-actions">
                    <button className="btn-outline" style={{ fontSize:"0.72rem" }} onClick={() => setModal({ type:"quiz", item:c })}>Quiz</button>
                    <button className="btn-outline" style={{ fontSize:"0.72rem", color:"var(--blue)", borderColor:"var(--blue)" }} onClick={() => setModal({ type:"quizStats", item:c })}>Stats</button>
                    <button className="btn-outline" onClick={() => setModal({ type:"class", item:c })}>Editar</button>
                    <button className="btn-danger"  onClick={() => askDelete("class", c.id, c.title, "clase")}>Eliminar</button>
                  </div>
                </div>
              ))}
              {classes.length === 0 && <p className="admin-empty">No hay clases.</p>}
            </div>
          </>
        )}

        {/* ── Artículos ── */}
        {tab === "articles" && !loading && (
          <>
            <div className="admin-toolbar">
              <h2>Artículos ({articles.length})</h2>
              <button className="btn-primary" onClick={() => setModal({ type:"article" })}>+ Nuevo artículo</button>
            </div>
            <div className="admin-list">
              {articles.map(a => (
                <div className="admin-item" key={a.id}>
                  <div className="admin-item-info">
                    <span className="admin-item-title">{a.title}</span>
                    <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap" }}>
                      <span className={`admin-badge ${a.is_published?"pub":"draft"}`}>{a.is_published?"Publicado":"Borrador"}</span>
                      {a.pdf_url && <span className="admin-badge" style={{ background:"#fdf3dc", color:"#a07800" }}>PDF</span>}
                    </div>
                  </div>
                  <div className="admin-item-actions">
                    <button className="btn-outline" onClick={() => setModal({ type:"article", item:a })}>Editar</button>
                    <button className="btn-danger"  onClick={() => askDelete("article", a.id, a.title, "artículo")}>Eliminar</button>
                  </div>
                </div>
              ))}
              {articles.length === 0 && <p className="admin-empty">No hay artículos.</p>}
            </div>
          </>
        )}

        {/* ── Recursos ── */}
        {tab === "resources" && !loading && (
          <>
            <div className="admin-toolbar">
              <h2>Recursos ({resources.length})</h2>
              <button className="btn-primary" onClick={() => setModal({ type:"resource" })}>+ Nuevo recurso</button>
            </div>
            <div className="admin-list">
              {resources.map(r => (
                <div className="admin-item" key={r.id}>
                  <div className="admin-item-info">
                    {r.thumbnail_url && <img src={r.thumbnail_url} alt="" style={{ width:44, height:44, objectFit:"cover", borderRadius:6, flexShrink:0 }} />}
                    <span className="admin-item-title">{TYPE_ICONS[r.type]} {r.title}</span>
                    <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap" }}>
                      <span className={`admin-badge ${r.is_published?"pub":"draft"}`}>{r.is_published?"Publicado":"Borrador"}</span>
                      <span className="admin-badge" style={{ background:"#eef2f2", color:"var(--teal-dark)" }}>{r.type}</span>
                    </div>
                  </div>
                  <div className="admin-item-actions">
                    <a href={r.url} target="_blank" rel="noreferrer" className="btn-outline" style={{ fontSize:"0.72rem", textDecoration:"none" }}>Ver</a>
                    <button className="btn-outline" onClick={() => setModal({ type:"resource", item:r })}>Editar</button>
                    <button className="btn-danger"  onClick={() => askDelete("resource", r.id, r.title, "recurso")}>Eliminar</button>
                  </div>
                </div>
              ))}
              {resources.length === 0 && <p className="admin-empty">No hay recursos.</p>}
            </div>
          </>
        )}

        {tab === "sidebar"  && isAdmin && <SidebarManager />}
        {tab === "students" && isAdmin && <StudentManager />}
        {tab === "progress" && <StudentStats />}
        {tab === "stats"    && <StatsTab isAdmin={isAdmin} />}

        {/* ── Colaboradores ── */}
        {tab === "collaborators_tab" && isAdmin && (
          <>
            <div className="admin-toolbar">
              <h2>Colaboradores ({collaborators.length})</h2>
              <button className="btn-primary" onClick={() => setModal({ type:"collaborator" })}>+ Nuevo colaborador</button>
            </div>
            {loading && <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Cargando...</p>}
            <div className="admin-list">
              {collaborators.map(c => (
                <div className="admin-item" key={c.id}>
                  <div className="admin-item-info">
                    <span className="admin-item-title">{c.first_name} {c.last_name}</span>
                    <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap", alignItems:"center" }}>
                      <span style={{ fontSize:"0.72rem", color:"var(--text-muted)" }}>{c.email}</span>
                      {c.must_change_password
                        ? <span className="admin-badge" style={{ background:"#fdf3dc", color:"#a07800" }}>Pendiente cambio clave</span>
                        : <span className="admin-badge pub">Activo</span>
                      }
                    </div>
                  </div>
                  <div className="admin-item-actions">
                    <button className="btn-outline" onClick={() => setModal({ type:"collaborator", item:c })}>Editar</button>
                    <button className="btn-danger"  onClick={() => askDelete("collaborator", c.id, `${c.first_name} ${c.last_name}`, "colaborador")}>Eliminar</button>
                  </div>
                </div>
              ))}
              {!loading && collaborators.length === 0 && <p className="admin-empty">No hay colaboradores.</p>}
            </div>
          </>
        )}
      </div>

      {/* Modales */}
      {modal?.type === "class"        && <ClassForm         initial={modal.item} onSave={handleSaveClass}        onCancel={() => setModal(null)} />}
      {modal?.type === "article"      && <ArticleForm       initial={modal.item} onSave={handleSaveArticle}      onCancel={() => setModal(null)} />}
      {modal?.type === "resource"     && <ResourceForm      initial={modal.item} onSave={handleSaveResource}     onCancel={() => setModal(null)} />}
      {modal?.type === "quiz"         && <QuizModal         classId={modal.item.id}                              onClose={() => setModal(null)} />}
      {modal?.type === "quizStats"    && <QuizStatsModal    classId={modal.item.id} title={modal.item.title}     onClose={() => setModal(null)} />}
      {modal?.type === "collaborator" && <CollaboratorForm  initial={modal.item} onSave={handleSaveCollaborator} onCancel={() => setModal(null)} />}

      {confirmDelete && (
        <ConfirmDeleteModal
          name={confirmDelete.name}
          type={confirmDelete.label}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}